import { NavLink, Outlet } from "react-router-dom";
import { Camera, Wifi, Smartphone, Check, CloudOff, RefreshCw, AlertTriangle } from "lucide-react";
import { cn } from "@/design/cn";
import { useActiveProfile, useProjects } from "@/domain/hooks";
import { useStore } from "@/domain/store";
import { useOnlineStatus } from "@/domain/useOnlineStatus";
import { useConnectionType } from "@/domain/useConnectionType";
import { useOfflineQueue, flushQueue, retryAction, discardAction } from "@/domain/offlineQueue";
import type { PendingAction } from "@/domain/offlineDb";
import type { Project } from "@/domain/types";
import { ImpactLedgerMark } from "@/shell/ImpactLedgerLogo";
import { ProfileSwitcher } from "@/shell/ProfileSwitcher";
import { NotificationsBell } from "@/shell/NotificationsDrawer";
import { reporterNav as NAV } from "@/shell/navSections";
import { useRoleGuide, RoleGuideTriggerIcon } from "@/shell/RoleGuideProvider";

const ACTION_LABELS: Record<string, string> = {
  raiseSpend: "Spend ask",
  submitEvidence: "Evidence submission",
};

/**
 * Responsive reporter surface: a focused mobile column with bottom nav, and a
 * wider web-portal layout on desktop with a side rail. The profile switcher in
 * the top bar is the way back out to the rest of the platform.
 */
export function ReporterShell() {
  const profile = useActiveProfile();
  const projects = useProjects();
  // "Simulate offline" (Settings) stays available as a QA convenience, but
  // real connectivity now also drives this - previously a genuine dropped
  // connection was invisible to this screen entirely, since nothing here
  // read navigator.onLine.
  const isOnline = useOnlineStatus();
  const connectionType = useConnectionType();
  const simulatedOffline = useStore((s) => s.reporterOffline);
  const offline = simulatedOffline || !isOnline;
  const syncOnlyOnWifi = useStore((s) => s.syncOnlyOnWifi);
  const { pending, syncing, failed } = useOfflineQueue();
  const isSyncing = syncing.length > 0;
  const queueTotal = pending.length + syncing.length + failed.length;
  const { openGuide } = useRoleGuide();
  // Pending items sit un-synced for two very different reasons, and the
  // banner must not conflate them: genuinely offline, vs. genuinely online
  // but paused by "Sync only on Wi-Fi" (Profile) because we're on a
  // confidently-detected cellular connection. The latter previously showed
  // the same "saved offline" wording, which reads as a connectivity problem
  // when the user is actually online and the setting is just doing its job.
  const waitingForWifi = !offline && pending.length > 0 && syncOnlyOnWifi && connectionType === "cellular";

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-bg">
      {/* Top bar — amber tinted when offline to signal status at a glance */}
      <header className={cn("flex h-14 shrink-0 items-center gap-3 border-b px-4 transition-colors", offline ? "border-warn/30 bg-warnSoft" : "border-line bg-card")}>
        <ImpactLedgerMark size={26} />
        <span className="hidden text-sm font-medium text-ink sm:inline">Field portal</span>
        <span className="mx-1 hidden h-5 w-px bg-line sm:inline" aria-hidden />
        <div className="min-w-0 flex-1 truncate">
          <span className="block truncate text-sm font-semibold text-ink">{projects[0]?.name ?? "Field"}</span>
          <span className="block text-[11px] text-faint">Active site</span>
        </div>
        <SyncPill status={offline ? "offline" : isSyncing ? "syncing" : "synced"} connectionType={connectionType} />
        <button
          onClick={openGuide}
          aria-label="How this works for you"
          className="flex shrink-0 items-center gap-1.5 rounded-md px-1.5 py-1.5 text-faint transition-colors hover:bg-subtle hover:text-ink"
        >
          <RoleGuideTriggerIcon className="h-[18px] w-[18px]" aria-hidden />
          <span className="hidden text-sm sm:inline">Guide</span>
        </button>
        <span className="hidden text-sm text-mute md:inline">{profile.displayName}</span>
        <NotificationsBell />
        <ProfileSwitcher />
      </header>

      <div className="flex min-h-0 flex-1 overflow-hidden">
        {/* Desktop side rail (web portal) */}
        <nav className="hidden w-52 shrink-0 flex-col gap-1 border-r border-line bg-card p-3 md:flex">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              className={({ isActive }) =>
                cn(
                  "flex h-10 items-center gap-3 rounded-md px-3 text-sm",
                  isActive ? "bg-brandSoft font-medium text-brand" : "text-mute hover:bg-subtle hover:text-ink",
                )
              }
            >
              <n.icon className="h-4 w-4" />
              {n.label}
            </NavLink>
          ))}
        </nav>

        {/* Content */}
        <main className="min-w-0 flex-1 overflow-y-auto pb-20 md:pb-0">
          {(offline || pending.length > 0 || isSyncing) && (
            <div className="mx-auto w-full max-w-2xl px-4 pt-3">
              <div className="flex items-center justify-between gap-3 rounded-md border border-warn/30 bg-warnSoft px-3 py-2 text-sm text-warn">
                <span className="flex items-center gap-2">
                  {isSyncing ? (
                    <RefreshCw className="h-4 w-4 animate-spin" />
                  ) : waitingForWifi ? (
                    <Wifi className="h-4 w-4" />
                  ) : (
                    <CloudOff className="h-4 w-4" />
                  )}
                  {isSyncing
                    ? `Syncing ${syncing.length} change${syncing.length === 1 ? "" : "s"}…`
                    : waitingForWifi
                      ? `${pending.length} change${pending.length === 1 ? "" : "s"} ready — waiting for Wi-Fi.`
                      : pending.length > 0
                        ? `${pending.length} change${pending.length === 1 ? "" : "s"} saved offline, waiting to sync.`
                        : "You are offline. Captures will be saved and synced later."}
                </span>
                {pending.length > 0 && !isSyncing && (
                  <button
                    onClick={() => { void flushQueue(); }}
                    disabled={offline}
                    className="shrink-0 rounded-md bg-warn px-2.5 py-1 text-xs font-medium text-white hover:opacity-90 disabled:opacity-50"
                  >
                    Sync now
                  </button>
                )}
              </div>
            </div>
          )}
          {failed.length > 0 && (
            <div className="mx-auto w-full max-w-2xl px-4 pt-3">
              <div className="rounded-md border border-bad/30 bg-badSoft px-3 py-2">
                <p className="mb-2 flex items-center gap-2 text-sm font-medium text-bad">
                  <AlertTriangle className="h-4 w-4" /> Needs attention — {failed.length} change{failed.length === 1 ? "" : "s"} didn't sync
                </p>
                <ul className="space-y-2">
                  {failed.map((action) => (
                    <FailedActionRow key={action.id} action={action} projects={projects} />
                  ))}
                </ul>
              </div>
            </div>
          )}
          <div className="mx-auto w-full max-w-2xl">
            <Outlet />
          </div>
        </main>
      </div>

      {/* Quick capture FAB — 24px above the bottom nav, mobile only. Badge shows the whole queue. */}
      <NavLink
        to="/reporter/evidence"
        aria-label={queueTotal > 0 ? `Quick capture — ${queueTotal} pending sync` : "Quick capture"}
        className="focus-ring fixed bottom-[88px] right-4 z-20 flex h-14 w-14 items-center justify-center rounded-full bg-brand text-white shadow-overlay md:hidden"
      >
        <Camera className="h-6 w-6" aria-hidden />
        {queueTotal > 0 && (
          <span className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-bad text-[10px] font-bold text-white tabular-nums" aria-hidden>
            {queueTotal > 9 ? "9+" : queueTotal}
          </span>
        )}
      </NavLink>

      {/* Mobile bottom nav — ink, signal active, 44px targets (P0.2) */}
      <nav
        className="fixed bottom-0 left-0 right-0 z-10 border-t border-inkBorder bg-ink900 md:hidden"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        <div className="grid h-16 grid-cols-4">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              className={({ isActive }) =>
                cn("flex min-h-[52px] flex-col items-center justify-center gap-1 text-[11px]", isActive ? "text-white" : "text-inkText3")
              }
            >
              {({ isActive }) => (
                <>
                  <n.icon className={cn("h-6 w-6", isActive && "text-signal")} />
                  {n.label}
                </>
              )}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}

function SyncPill({
  status,
  connectionType,
}: {
  status: "offline" | "syncing" | "synced";
  connectionType: "wifi" | "cellular" | "unknown";
}) {
  if (status === "offline") {
    return (
      <span className="flex items-center gap-1 rounded-full bg-subtle px-2 py-0.5 text-[11px] text-faint">
        <CloudOff className="h-3 w-3" /> Offline
      </span>
    );
  }
  if (status === "syncing") {
    return (
      <span className="flex items-center gap-1 rounded-full bg-warnSoft px-2 py-0.5 text-[11px] text-warn">
        <RefreshCw className="h-3 w-3 animate-spin" /> Syncing
      </span>
    );
  }
  // "Synced" doesn't imply wifi-only - most browsers (notably Safari/iOS)
  // don't expose connection type at all, so a plain "Online" is the honest
  // default; the Wifi/Smartphone icon only appears where the browser is
  // actually willing to say which one it is.
  const label = connectionType === "wifi" ? "Wifi" : connectionType === "cellular" ? "Mobile data" : "Online";
  const Icon = connectionType === "wifi" ? Wifi : connectionType === "cellular" ? Smartphone : Check;
  return (
    <span className="flex items-center gap-1 rounded-full bg-okSoft px-2 py-0.5 text-[11px] text-ok">
      <Icon className="h-3 w-3" /> {label}
    </span>
  );
}

// Matches assertCanAct's raw rejection (server/helpers/approvalRoles.js) for
// a raise: "User 9 is not authorised to raise on project 2". This action's
// userId is always the current actor — assertCanAct is only ever called
// with the acting user's own id — so there's no ambiguity in softening it
// to "you". Any other message (a different action, a genuinely unexpected
// error) falls through to the raw server text unchanged; this is a targeted
// improvement for one known, common rejection, not a general re-parser.
const RAISE_NOT_AUTHORISED = /is not authorised to raise on project (\S+)/;

export function friendlyLastError(action: PendingAction, projects: Project[]): string | null {
  if (!action.lastError) return null;
  if (action.type !== "raiseSpend") return action.lastError;
  const match = action.lastError.match(RAISE_NOT_AUTHORISED);
  if (!match) return action.lastError;
  const project = projects.find((p) => String(p.id) === match[1]);
  return project
    ? `You're not authorised to raise asks on ${project.name} — check you're still a member of this project.`
    : action.lastError;
}

/** One row in the "Needs attention" list — a queued action whose replay
 * failed, showing the server's own message (softened where we can resolve
 * it to something a reporter can act on). Never auto-retried; Retry/Discard
 * are the only ways this row moves, both explicit taps. */
function FailedActionRow({ action, projects }: { action: PendingAction; projects: Project[] }) {
  const message = friendlyLastError(action, projects);
  return (
    <li className="rounded-md border border-bad/20 bg-card px-3 py-2">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium text-ink">{ACTION_LABELS[action.type] ?? action.type}</span>
        <div className="flex shrink-0 gap-1.5">
          <button
            onClick={() => { void retryAction(action.id); }}
            className="rounded-md bg-bad px-2 py-1 text-[11px] font-medium text-white hover:opacity-90"
          >
            Retry
          </button>
          <button
            onClick={() => { void discardAction(action.id); }}
            className="rounded-md border border-line px-2 py-1 text-[11px] font-medium text-mute hover:bg-subtle"
          >
            Discard
          </button>
        </div>
      </div>
      {message && <p className="mt-1 text-xs text-mute">{message}</p>}
    </li>
  );
}
